<!-- Copyright (C) 2025 Skylark Drones -->
<template>
  <div>
    <home-page-section-header v-if="docks.length > 0" title="Docks" />
    <v-row no-gutters>
      <v-col cols="12">
        <v-row>
          <v-col
            v-for="(dock, i) in docks"
            :key="dock.id"
            cols="8"
            sm="6"
            md="4"
            lg="3"
          >
            <dock-card
              :dock="dock"
              :dock-state="dockStates[i]"
              :to="{
                name: 'Dock',
                params: { dockId: dock.id },
                query: { source: source.DOCK_CARD }
              }"
              @edit="event => onEdit(event, i)"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <dock-actions-dialog
      v-if="dockIdx !== -1"
      v-model="dockEditField"
      :dock="docks[dockIdx]"
      :edit-mode="editMode"
      :loading="isUpdatingDock"
      @update="patchDock"
      @close="dockIdx = -1"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import DockCard from '@/components/homepage/docks-row/DockCard.vue'
import DockActionsDialog from './DockActionsDialog.vue'
import HomePageSectionHeader from '@/components/homepage/HomePageSectionHeader.vue'
import {
  getAllDocks,
  getDockState,
  updateDock,
  getDock
} from '@/services/DockService'
import { source } from '@/utils/analytics/abstraction/eventEnums'

export default {
  name: 'DockRow',

  components: {
    DockCard,
    HomePageSectionHeader,
    DockActionsDialog
  },

  data() {
    return {
      source,
      docks: [],
      dockStates: [],
      pollTimer: null,
      editMode: '',
      dockEditField: '',
      dockIdx: -1,
      isUpdatingDock: false
    }
  },

  computed: {
    ...mapGetters(['currentActiveOrganizationId']),
    dockSnList() {
      return this.docks.map(d => d.sn)
    }
  },

  watch: {
    currentActiveOrganizationId: {
      immediate: true,
      handler: function () {
        if (this.currentActiveOrganizationId) {
          this.fetchDocks()
        }
      }
    }
  },

  destroyed() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
    }
  },

  methods: {
    ...mapMutations(['updateNotificationPayload']),
    startPollingDockState() {
      this.pollTimer = setInterval(() => this.fetchDockStates(), 10 * 1000)
    },
    onEdit(event, idx) {
      this.editMode = event
      this.dockEditField = this.docks[idx][event]
      this.dockIdx = idx
    },
    async patchDock() {
      this.isUpdatingDock = true

      try {
        const payload = { [this.editMode]: this.dockEditField }

        const res = await updateDock(
          this.docks[this.dockIdx].id,
          payload,
          this.docks[this.dockIdx].etag
        )

        this.docks[this.dockIdx].etag = res.headers.etag
        this.docks[this.dockIdx][this.editMode] = this.dockEditField

        this.dockIdx = -1
      } catch (err) {
        console.error(err)

        if (err.response?.status === 412) {
          const dockResp = await getDock(this.docks[this.dockIdx].id)
          this.docks[this.dockIdx].etag = dockResp.headers.etag
          this.patchDock()
          return
        }

        this.updateNotificationPayload({
          message: 'Unable to update dock!',
          code: 400,
          timeout: 5000
        })
      } finally {
        this.isUpdatingDock = false
      }
    },
    async fetchDocks() {
      try {
        const resp = await getAllDocks()
        this.docks = resp.data.docks

        if (!this.docks.length) {
          return
        }

        await this.fetchDockStates()

        if (!this.pollTimer) {
          this.startPollingDockState()
        }
      } catch (err) {
        this.updateNotificationPayload({
          message: 'Unable to fetch docks!',
          code: 400,
          timeout: 5000
        })
      }
    },
    async fetchDockStates() {
      try {
        const stateResp = await getDockState(this.dockSnList)
        this.dockStates = stateResp.data.docks
      } catch (err) {
        this.updateNotificationPayload({
          message: 'Unable to fetch dock states!',
          code: 400,
          timeout: 5000
        })
      }
    }
  }
}
</script>
